import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { FormField } from "@/components/ui";

export interface TramitadorAutorizado {
  id: string;
  razonSocial: string;
  rfc: string;
}

interface RegistroResumen {
  id: string;
  razonSocial: string;
  rfc: string;
  estatus: string;
}

/** Vista de solo-lectura de una parte (titular/tramitador) fijada por el sistema. */
export function ParteBloqueada({ razonSocial, nota }: { razonSocial: string; nota: string }) {
  return (
    <div className="flex items-start justify-between gap-2 rounded border bg-surface-2 px-3 py-2 text-sm">
      <div className="min-w-0">
        <div className="font-medium text-fg">{razonSocial}</div>
        <div className="mt-0.5 text-xs text-fg-subtle">{nota}</div>
      </div>
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-fg-faint" aria-label="No editable" />
    </div>
  );
}

/**
 * Campo «Tramitador» derivado del Registro Legal (cartas poder vigentes del
 * titular seleccionado). La relación Titular↔Tramitador se administra
 * exclusivamente en el módulo Registro Legal; el alta de solicitudes solo la
 * consume:
 *
 * - 0 tramitadores autorizados → no renderiza nada: el titular actúa directo.
 * - 1 → se asigna automáticamente y se muestra en solo lectura.
 * - >1 → selector restringido únicamente a los autorizados.
 *
 * No usar cuando el contexto del usuario externo ya fija el tramitador (carta
 * poder propia o tramitador puro): en ese caso mostrar {@link ParteBloqueada}.
 */
export function TramitadorAutorizadoField({
  titularId,
  value,
  onChange,
  onAutorizadosChange,
}: {
  titularId: string | null | undefined;
  value: RegistroResumen | null;
  onChange: (t: RegistroResumen | null) => void;
  /** Notifica cuántos tramitadores autorizados tiene el titular (para validar pasos). */
  onAutorizadosChange?: (n: number) => void;
}) {
  const q = useQuery({
    queryKey: ["tramitadores-autorizados", titularId],
    queryFn: async () =>
      (await api.get<TramitadorAutorizado[]>(
        `/api/registros-legales/${titularId}/tramitadores-autorizados`,
      )).data,
    enabled: !!titularId,
    staleTime: 60_000,
  });
  const autorizados = q.data ?? [];

  useEffect(() => {
    if (!titularId) onAutorizadosChange?.(0);
    else if (q.data) onAutorizadosChange?.(q.data.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titularId, q.data]);

  // Sincroniza la selección con la lista autorizada: autoasigna cuando hay
  // exactamente uno y limpia selecciones que dejaron de ser válidas (p. ej.
  // al cambiar de titular).
  useEffect(() => {
    if (!titularId) {
      if (value) onChange(null);
      return;
    }
    if (!q.data) return;
    if (q.data.length === 1) {
      if (value?.id !== q.data[0].id) onChange(aRegistro(q.data[0]));
    } else if (value && !q.data.some((t) => t.id === value.id)) {
      onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titularId, q.data]);

  if (!titularId) return null;
  if (q.isLoading) {
    return (
      <FormField label="Tramitador">
        <div className="flex items-center gap-2 rounded border bg-surface-2 px-3 py-2 text-sm text-fg-subtle">
          <Loader2 className="h-4 w-4 animate-spin" /> Consultando Registro Legal…
        </div>
      </FormField>
    );
  }
  if (autorizados.length === 0) return null;

  if (autorizados.length === 1) {
    return (
      <FormField label="Tramitador">
        <ParteBloqueada
          razonSocial={autorizados[0].razonSocial}
          nota="Asignado automáticamente: carta poder vigente registrada en Registro Legal. Si necesitas cambiarlo, actualiza la carta poder en ese módulo."
        />
      </FormField>
    );
  }

  return (
    <FormField label="Tramitador *">
      <select
        value={value?.id ?? ""}
        onChange={(e) => {
          const t = autorizados.find((x) => x.id === e.target.value);
          onChange(t ? aRegistro(t) : null);
        }}
        className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        <option value="">— Selecciona —</option>
        {autorizados.map((t) => (
          <option key={t.id} value={t.id}>
            {t.razonSocial} ({t.rfc})
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-fg-subtle">
        Este titular tiene {autorizados.length} tramitadores con carta poder vigente; la solicitud debe
        usar uno de ellos. La relación se administra en Registro Legal.
      </p>
    </FormField>
  );
}

function aRegistro(t: TramitadorAutorizado): RegistroResumen {
  return { id: t.id, razonSocial: t.razonSocial, rfc: t.rfc, estatus: "" };
}
